import { generateCode } from "@examspark/common";
import {
  AuthorizationContextInput,
  CreateUserInput,
  ModelResourceConnection,
  MutationCreateWorkspaceRegistrationCodeArgs,
  QueryListWorkspaceRegistrationCodesArgs,
  Resource,
  TagInput,
  User,
  Workspace,
  WorkspaceRegistrationCode,
  WorkspaceUserRole,
} from "@examspark/types";

import GraphQLAPI, { queries } from "..";
import { typedGql, typedGqlMutation } from "../utils";

import { deleteResource } from "./resource";

const fragmentBasic = `
id
sk
name`;

export const createRegistrationDomain = async (
  workspaceId: string,
  domain: string,
  tags?: TagInput[],
) => {
  return await GraphQLAPI.client.query<{ createWorkspace: Workspace }>({
    query: /* GraphQL */ `
      mutation CreateWorkspace($input: CreateWorkspaceInput!) {
        createWorkspace(input: $input) {
          ${fragmentBasic}
        }
      }
    `,
    variables: {
      input: {
        id: domain.toLowerCase(),
        sk: `registration-domain#${workspaceId}`,
        tags,
        workspaceId,
      },
    },
  });
};

export const deleteRegistrationDomain = async (
  workspaceId: string,
  domain: string,
) => {
  return await deleteResource({
    id: domain,
    sk: `registration-domain#${workspaceId}`,
    workspaceId,
  });
};

export const deleteRegistrationCode = async (
  workspaceId: string,
  code: string,
) => {
  return await deleteResource({
    id: workspaceId,
    sk: ["registration-code", code].join("#"),
    workspaceId,
  });
};

export const createRegistrationCode = async (
  workspaceId: string,
  code?: string,
  role?: WorkspaceUserRole,
) => {
  if (!code) code = generateCode();

  return await GraphQLAPI.client.query<{ createWorkspace: Workspace }>({
    query: /* GraphQL */ `
      mutation CreateWorkspaceRegistrationCode(
        $input: CreateWorkspaceRegistrationCodeInput!
      ) {
        createWorkspaceRegistrationCode(input: $input) {
          id
          sk
          role
        }
      }
    `,
    variables: {
      input: {
        id: workspaceId,
        sk: `registration-code#${code}`,
        schemaVersion: "2020-01-01",
        role,
        workspaceId,
      },
    } as MutationCreateWorkspaceRegistrationCodeArgs,
  });
};

export const listRegistrationDomains = async (workspaceId: string) => {
  const result = await GraphQLAPI.client.query<{
    listResourcesByWorkspaceId: ModelResourceConnection;
  }>({
    query: /* GraphQL */ `
      query ListResourcesByWorkspaceId(
        $workspaceId: String
        $sk: ModelStringKeyConditionInput
        $sortDirection: ModelSortDirection
        $filter: ModelResourceFilterInput
        $limit: Int
        $nextToken: String
      ) {
        listResourcesByWorkspaceId(
          workspaceId: $workspaceId
          sk: $sk
          sortDirection: $sortDirection
          filter: $filter
          limit: $limit
          nextToken: $nextToken
        ) {
          items {
            id
            sk
            tags {
              key
              type
              value
            }
          }
          nextToken
        }
      }
    `,
    variables: {
      workspaceId,
      sk: {
        beginsWith: `registration-domain`,
      },
    },
  });

  return result.data?.listResourcesByWorkspaceId;
};

export const listRegistrationCodes = async (
  workspaceId: string,
  authorizationContext?: AuthorizationContextInput,
) => {
  const result = await GraphQLAPI.client.query<{
    listWorkspaceRegistrationCodes: WorkspaceRegistrationCode[];
  }>({
    query: /* GraphQL */ `
      query ListRegistrationCodes(
        $input: ListResourcesByWorkspaceIdInput
        $authorizationContext: AuthorizationContextInput
      ) {
        listWorkspaceRegistrationCodes(
          input: $input
          authorizationContext: $authorizationContext
        ) {
          id
          sk
          role
        }
      }
    `,
    variables: {
      input: { workspaceId },
      authorizationContext,
    } as QueryListWorkspaceRegistrationCodesArgs,
  });

  return result.data?.listWorkspaceRegistrationCodes;
};

/** List all workspaces that whitelist a given domain. Returns
 * `registration-domain` type records. */
export const listRegistrationDomainWorkspaces = async (domain: string) => {
  const result = await GraphQLAPI.client.query<{
    listResources: ModelResourceConnection;
  }>({
    query: /* GraphQL */ `
      query ListResources(
        $id: ID
        $sk: ModelStringKeyConditionInput
        $sortDirection: ModelSortDirection
        $filter: ModelResourceFilterInput
        $limit: Int
        $nextToken: String
      ) {
        listResources(
          id: $id
          sk: $sk
          sortDirection: $sortDirection
          filter: $filter
          limit: $limit
          nextToken: $nextToken
        ) {
          items {
            id
            sk
            workspaceId
          }
          nextToken
        }
      }
    `,
    variables: {
      id: domain,
      sk: {
        beginsWith: `registration-domain`,
      },
    },
  });

  return result.data?.listResources;
};

export const determineRegistrationStatus = async ({
  workspaceId,
  email,
  code,
}: {
  workspaceId: string;
  email: string;
  code: string;
}) => {
  return await typedGql(queries.user.determineRegistrationStatus)({
    input: {
      action: "determine-registration-status",
      params: {
        email: email.trim(),
        registrationCode: code.trim(),
        workspaceId: workspaceId.trim(),
      },
    },
  });
};

export const registerCognitoUser = async ({
  jwt,
  firstName,
  lastName,
}: {
  jwt: string;
  firstName: string;
  lastName: string;
}) => {
  return await typedGqlMutation(queries.user.registerCognitoUser)({
    input: {
      action: "register-cognito-user",
      params: {
        jwt,
        firstName,
        lastName,
      },
    },
  });
};

export const registerAppUser = async ({ jwt }: { jwt: string }) => {
  return await typedGqlMutation(queries.user.registerAppUser)({
    input: {
      action: "register-app-user",
      params: {
        jwt,
      },
    },
  });
};

export const createAccount = async (user: CreateUserInput) => {
  const result = await GraphQLAPI.client.query<{ createAccount: User }>({
    query: /* GraphQL */ `
      mutation CreateAccount($input: CreateAccountInput!) {
        createAccount(input: $input) {
          id
          sk
          firstName
          lastName
          email
          avatarHash
          acl
          createdAt
          updatedAt
          userId
          lastActiveAt
          status
          type
          title
          body
          data
          workspaceId
          eventId
          jobId
          intercomId
        }
      }
    `,
    variables: {
      input: user,
    },
  });

  return result.data?.createAccount;
};

export const getRegistrationCode = async (
  workspaceId: string,
  code: string,
) => {
  const result = await GraphQLAPI.client.query<{ getResource: Resource }>({
    query: /* GraphQL */ `
      query GetResource($id: ID!, $sk: String!) {
        getResource(id: $id, sk: $sk) {
          id
          sk
        }
      }
    `,
    variables: {
      id: workspaceId,
      sk: ["registration-code", code].join("#"),
    },
  });

  return result.data?.getResource;
};
